import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import styles from "./image-with-load-spinner.styles.module.scss";

export function ImageWithLoadSpinner({
  src,
  alt = "",
  fallbackSrc,
  wrapperClassName = "",
  imgClassName = "",
  objectFit = "contain",
  loading = "lazy",
  decoding = "async",
  draggable = false,
  showSpinner = true,
  devDelayRevealMs = 0,
}) {
  const [displaySrc, setDisplaySrc] = useState(() => src || fallbackSrc);
  const [loaded, setLoaded] = useState(false);
  const revealTimeoutRef = useRef(null);

  useEffect(() => {
    setDisplaySrc(src || fallbackSrc);
    setLoaded(false);
    if (revealTimeoutRef.current != null) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
  }, [src, fallbackSrc]);

  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current != null) {
        clearTimeout(revealTimeoutRef.current);
      }
    };
  }, []);

  const handleError = () => {
    if (revealTimeoutRef.current != null) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    if (fallbackSrc && displaySrc !== fallbackSrc) {
      setDisplaySrc(fallbackSrc);
      setLoaded(false);
      return;
    }
    setLoaded(true);
  };

  const handleLoad = () => {
    const delay =
      import.meta.env.DEV && typeof devDelayRevealMs === "number" && devDelayRevealMs > 0 ? devDelayRevealMs : 0;
    if (delay > 0) {
      revealTimeoutRef.current = window.setTimeout(() => {
        revealTimeoutRef.current = null;
        setLoaded(true);
      }, delay);
      return;
    }
    setLoaded(true);
  };

  return (
    <span className={`${styles.host} ${wrapperClassName}`.trim()}>
      {showSpinner && !loaded && (
        <span
          className={styles.spinnerOverlay}
          aria-hidden
        >
          <span className={styles.spinner} />
        </span>
      )}
      <img
        src={displaySrc}
        alt={alt}
        className={`${styles.img} ${loaded ? styles.imgLoaded : ""} ${imgClassName}`.trim()}
        style={{objectFit}}
        loading={loading}
        decoding={decoding}
        draggable={draggable}
        onLoad={handleLoad}
        onError={handleError}
      />
    </span>
  );
}

ImageWithLoadSpinner.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  fallbackSrc: PropTypes.string,
  wrapperClassName: PropTypes.string,
  imgClassName: PropTypes.string,
  objectFit: PropTypes.oneOf(["contain", "cover", "fill", "none", "scale-down"]),
  loading: PropTypes.string,
  decoding: PropTypes.string,
  draggable: PropTypes.bool,
  showSpinner: PropTypes.bool,
  devDelayRevealMs: PropTypes.number,
};
